<template>
  <div class="pie-card">
    <div class="pie-wrap">
      <svg class="pie" viewBox="0 0 42 42" role="img" aria-label="Gráfico de pizza">
        <!-- anel de fundo -->
        <circle class="ring" cx="21" cy="21" r="15.9155" />

        <!-- segmentos -->
        <template v-for="(s, i) in segments" :key="s.key">
          <circle
            class="slice"
            :stroke="colors[i % colors.length]"
            cx="21" cy="21" r="15.9155"
            :stroke-dasharray="`${s.percent} ${100 - s.percent}`"
            :stroke-dashoffset="s.offset"
          />
        </template>

        <!-- centro com total -->
        <g class="center">
          <text x="50%" y="47%" text-anchor="middle" class="total-label">Total R$</text>
          <text x="50%" y="60%" text-anchor="middle" class="total-value">{{ Funcoes.formatDecimal(total) }}</text>
        </g>
      </svg>
    </div>

    <ul class="legend">
      <li v-for="(s, i) in segments" :key="`legend-${s.key}`">
        <span class="dot" :style="{ background: colors[i % colors.length] }"></span>
        <span class="label">{{ labelFor(s.key) }}</span>
        <span class="value">{{ formatBRL(s.value) }}</span>
        <span class="percent">({{ s.percent.toFixed(1) }}%)</span>
      </li>
    </ul>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { Funcoes } from '@/funcoes/utils'

/**
 * Props:
 * - data: objeto no formato { vlcf, vlnf, vlnp, vlns, vltt }
 * - labelsMap: rótulos amigáveis para as chaves
 * - colors: paleta opcional
 */
const props = defineProps({
  data: { type: Object, required: true },
  labelsMap: {
    type: Object,
    default: () => ({
      vlcf: 'CF',
      vlnf: 'NF',
      vlnp: 'NP',
      vlns: 'NS'
    })
  },
  colors: {
    type: Array,
    default: () => [
      '#4F46E5', '#16A34A', '#E11D48', '#F59E0B', '#0EA5E9', '#7C3AED'
    ]
  }
})

/** soma total (usa vltt se vier; senão soma as demais chaves) */
const total = computed(() => {
  const { vltt, ...rest } = props.data || {}
  const sumRest = Object.entries(rest)
    .filter(([k]) => k !== 'vltt')
    .reduce((acc, [, v]) => acc + (Number(v) || 0), 0)
  return Number(vltt ?? sumRest) || 0
})

/** entradas válidas (todas as chaves exceto vltt, com valor > 0) */
const entries = computed(() =>
  Object.entries(props.data || {})
    .filter(([k]) => k !== 'vltt')
    .map(([k, v]) => ({ key: k, value: Number(v) || 0 }))
    .filter(e => e.value > 0)
)

/**
 * SVG donut com múltiplos círculos:
 * - cada fatia usa stroke-dasharray = "<percent> 100-<percent>"
 * - stroke-dashoffset acumulado (começa em 25 para começar no topo)
 */
const segments = computed(() => {
  const t = total.value > 0 ? total.value : entries.value.reduce((a, b) => a + b.value, 0)
  let acc = 25 // deslocamento inicial (começa no topo)
  return entries.value.map((e) => {
    const p = t > 0 ? (e.value / t) * 100 : 0
    const seg = { key: e.key, value: e.value, percent: p, offset: acc * -1 }
    acc += p
    return seg
  })
})

function labelFor(key) {
  return props.labelsMap?.[key] ?? key
}
function formatBRL(n) {
  return (Number(n) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}
</script>

<style scoped>
.pie-card {
  display: grid;
  grid-template-columns: minmax(220px, 280px) 1fr;
  gap: 16px;
  align-items: center;
  padding: 16px;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(0,0,0,.05);
}

.pie-wrap { display: flex; justify-content: center; align-items: center; }
.pie { width: 220px; height: 220px; }

.ring {
  fill: none;
  stroke: #f3f4f6;
  stroke-width: 6;
}

.slice {
  fill: none;
  stroke-width: 6;
  transition: stroke-dashoffset .3s ease, stroke-dasharray .3s ease;
}

.center .total-label {
  font-size: 3.2px;
  fill: #6b7280;
}

.center .total-value {
  font-size: clamp(3px, 0.6vw + 2px, 4.5px);
  font-weight: 700;
  fill: #111827;
}

.legend {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 6px;
}

.legend li {
  display: grid;
  grid-template-columns:
    16px              /* bolinha */
    20px              /* label (tipo da nota) */
    100px             /* valor R$ */
    70px;             /* percentual */
  gap: 8px;
  align-items: center;
  font-size: 14px;
  font-family: system-ui, sans-serif;
}

.legend .dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
}

.legend .label {
  color: #111827;
  font-weight: 500;
  white-space: nowrap;
}

.legend .value {
  color: #111827;
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.legend .percent {
  color: #6b7280;
  text-align: right;
}

@media (max-width: 640px) {
  .pie-card { grid-template-columns: 1fr; }
  .pie { width: 200px; height: 200px; }
}
</style>
